// Third Party
import * as React from 'react';

// Utils
import {
  ListingReviewsListAllForListingQuery,
  useListingReviewsListAllForListingQuery,
} from '@gql';
import { useToggle } from '@hooks';
export type ReviewsContext = {
  /** Activate the reviews modal */
  activateReviewsModal: VoidFunction;
  /** Deactivate the reviews modal */
  deactivateReviewsModal: VoidFunction;
  /** Flag indicating whether or not the reviews modal is active */
  reviewsModalActive: boolean;
  /** Reviews results data */
  listingReviewsData?: ListingReviewsListAllForListingQuery;
  /** Reviews data loading flag */
  reviewsLoading: boolean;
  /** Current page number */
  page: number;
  /** Set the current page number */
  setPage: (page: number) => void;
};

const ReviewsContext = React.createContext<ReviewsContext>(undefined!);

type ReviewsProviderProps = {
  children: React.ReactNode;
  listingId: string;
};

export const ReviewsProvider: React.FC<ReviewsProviderProps> = ({
  children,
  listingId,
}) => {
  const {
    on: reviewsModalActive,
    setOn: activateReviewsModal,
    setOff: deactivateReviewsModal,
  } = useToggle(false);

  const [page, setPage] = React.useState(1);

  const { data: listingReviewsData, loading: reviewsLoading } =
    useListingReviewsListAllForListingQuery({
      variables: { id: listingId, page: page, pageSize: 20 },
    });

  const contextValue: ReviewsContext = {
    activateReviewsModal,
    deactivateReviewsModal,
    reviewsModalActive,
    listingReviewsData,
    reviewsLoading,
    page,
    setPage,
  };

  return (
    <ReviewsContext.Provider value={contextValue}>
      {children}
    </ReviewsContext.Provider>
  );
};

export const useReviews = () => {
  const context = React.useContext(ReviewsContext);

  if (context === undefined) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }

  return context;
};
